import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import './GoogleSearchAttribution.css';

// The backend supplies Google's original search_suggestions HTML, never model-written HTML.
// Keep this separate from source links and do not shorten or restyle the supplied widget.
export function googleSearchSuggestions(items = []) {
  const snippets = items.flatMap(item => {
    const grounding = item?.result?.grounding || item?.grounding;
    return grounding?.provider === 'google_search' && Array.isArray(grounding.searchSuggestionsHtml)
      ? grounding.searchSuggestionsHtml.filter(html => typeof html === 'string' && html.trim())
      : [];
  });
  return [...new Set(snippets)];
}

function SearchSuggestion({ html }) {
  const host = useRef(null);
  useEffect(() => {
    // A shadow root isolates Google's CSS from Bootstrap and the surrounding KYC form.
    // Sanitize even provider-supplied HTML: saved research is not executable app code.
    const root = host.current.shadowRoot || host.current.attachShadow({ mode: 'open' });
    const content = DOMPurify.sanitize(html, {
      RETURN_DOM_FRAGMENT: true,
      FORCE_BODY: true,
      ADD_TAGS: ['style'],
      FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'iframe', 'object', 'embed', 'base', 'link'],
    });
    content.querySelectorAll('a').forEach(link => {
      // Preserve the provider's URL, including its query string; do not add tracking.
      const href = link.getAttribute('href') || '';
      if (!/^https:\/\//i.test(href)) link.removeAttribute('href');
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
    root.replaceChildren(content);
    return () => root.replaceChildren();
  }, [html]);

  return <div ref={host} className="googleSearchAttributionWidget" />;
}

export default function GoogleSearchAttribution({ items }) {
  const snippets = googleSearchSuggestions(items);
  if (!snippets.length) return null;
  return (
    <div className="googleSearchAttribution" role="region" aria-label="Google Search suggestions">
      <div className="googleSearchAttributionLabel">Google Search suggestions</div>
      {snippets.map(html => <SearchSuggestion key={html} html={html} />)}
    </div>
  );
}
